export type QuickAccessItem = {
  id: string
  title: string
  icon: {
    url: string
  }
  color: string
  url: string
  is_disabled: boolean
}

export type PeopleSpotlightItem = CompanyPulseItem

export type PeopleSpotlightCategory = NewsCategory

export type CompanyPulseItem = {
  id: string
  slug: string
  categories?: NewsCategory[]
  content?: string
  hero_image?: {
    url: string
  }
  image_position: 'left' | 'center' | 'right'
  created_at: string
  short_description: string
  label: string
  created_by: {
    first_name: string
    last_name: string
  }
}

export type BaseCategory = {
  id: string
  title: string
  color?: string
  /** Shown as a hover tooltip when present — e.g. an admin-authored
   * explanation of what the category means (Kudos categories only). */
  description?: string | null
}

export type NewsCategory = BaseCategory
export type PolicyCategory = BaseCategory

export type PolicyItem = {
  id: string
  slug?: string
  title?: string
  description?: string
  category?: PolicyCategory
  created_at?: string
  modified_at?: string
  url?: string
  color?: string
  icon?: {
    url?: string
  }
}

export type CmsAsset = {
  id: string
  name: string
  content_type: string
  url: string
  width: number | null
  height: number | null
  size: number
}

type BenefitsPageIntroCard = {
  tittle: string
  icon: CmsAsset | null
  description: string | null
}

type BenefitsPageBenefitCard = {
  title: string
  description: string | null
  icon: CmsAsset | null
  color: string | null
  tier: BenefitsPageTier
  benefits_list: { benefit: string | null }[]
  additional_information?: string
}

export type BenefitsPageBenefitGroup = {
  benefit_card: BenefitsPageBenefitCard
}

type BenefitsPageTier = {
  title: string
  year_label: string | null
  description: string | null
  benefits_list: { benefit: string | null; is_new: boolean | null }[]
}

export type BenefitsPageTierProgression = {
  tier: BenefitsPageTier
}

export type BenefitsPageData = {
  id: string
  title: string
  intro_card: BenefitsPageIntroCard
  benefits_list: BenefitsPageBenefitGroup[]
  tier_progression: BenefitsPageTierProgression[]
  slug: string
  publish_date: string
  created_at: string
  modified_at: string
  status: string
  type_id: string
}

type ResourceCard = {
  title: string
  description: string
  icon: CmsAsset
  page_url: {
    slug: string
  }
}

export type GalleryImage = {
  id: string
  name: string
  url: string
  width: number | null
  height: number | null
}

export type GalleryFolder = {
  folder_name: string
  images: GalleryImage[]
}

export type ResourcesPageData = {
  id: string
  title: string
  intro: string
  resources_cards: { cards: ResourceCard }[]
  gallery?: {
    gallery: {
      folder_name: string
      images: { images: CmsAsset }[]
    }
  }[]
}

export type ResourcesPageSlugData = {
  id: string
  content: string
  title: string
}

export type ThankYouPageData = {
  id: string
  title: string
  subtitle: string
  last_x_days?: number
  thank_you_section: ThankYouSectionData
}

export type ThankYouSectionData = {
  thank_you_section_title: string
  thank_you_section_sub_title: string
  items: ThankYouCardItem[]
}

export type ThankYouCardItem = {
  id: string
  name: string
  position: string
  text: string
  // Tenure at EBS (CMS field). May be a number ("4") or a string ("4 years").
  years?: number | string | null
  image?: { url: string } | null
}

export type NewsletterEntry = {
  title: string
  pdf_file: CmsAsset
  year: number
  cover_image?: {
    url: string
  }
}

export type NewsletterItemBlock = {
  code_name: string
  content_type_id: string
  data: NewsletterEntry
}

export type NewslettersPageData = {
  id: string
  title: string
  subtitle: string
  items: NewsletterItemBlock[]
  slug: string
  publish_date: string
  created_at: string
  modified_at: string
  status: string
  type_id: string
}

export const HOME_PAGE_COLUMN = {
  main: 'main',
  sidebar: 'sidebar'
} as const

export type HomePageBlockColumn =
  (typeof HOME_PAGE_COLUMN)[keyof typeof HOME_PAGE_COLUMN]

export type HomePageBlock =
  | ''
  | 'greeting'
  | 'company_pulse'
  | 'birthdays'
  | 'work_anniversaries'
  | 'new_team_members'
  | 'calendar'
  | 'quick_acces'
  | 'days_off'
  | 'open_roles'
  | 'intranet_suggestions'
  | 'suggestions_box'
  | 'work_tools'
  | 'kudos'

export type HomePageBlockLayout = {
  block: HomePageBlock
  column: HomePageBlockColumn
}

type HomePageAuthor = {
  id: string
  first_name: string
  last_name: string
}

export type HomePageData = {
  id: string
  title: string
  slug: string
  blocks_layout: HomePageBlockLayout[]
  news: { news: CompanyPulseItem }[]
  publish_date: string
  created_at: string
  modified_at: string
  status: string
  type_id: string
  created_by: HomePageAuthor
  modified_by: HomePageAuthor
}
